/* iter55z-q — Contextual tour registry.
 *
 * Central catalogue of on-demand tours. Each entry is a short array of
 * spotlight steps keyed to `data-testid` anchors elsewhere in the app.
 *
 * Usage from any component:
 *     import { runTour } from '@/lib/tours';
 *     runTour('character-bible-open');       // no-op if already seen
 *     runTour('character-bible-open', { force: true });  // re-run on demand
 */
import { startContextualTour, resetTour } from '@/components/GuidedTour';

/* iter55f56 — Prompt-mode helper. `runTour` short-circuits when the
 * user picked "Off" so contextual tours never fire against a veteran.
 * The initial `onboarding-start` walkthrough always runs since it is
 * explicitly requested by the mode-picker itself. */
function promptMode() {
  try { return window.localStorage.getItem('pw_prompt_mode') || 'guided'; }
  catch { return 'guided'; }
}

export const TOURS = {
  /* ── Creation Journey — new-writer walkthrough ─────────────────────────
   * Fires from CreationJourneyCard. Each single-step mini-tour spotlights
   * one tool. The card itself tracks progress; tour keys here are 1-step
   * so the spotlight fires immediately on "Go" without a multi-step flow.
   * ─────────────────────────────────────────────────────────────────────── */
  'cj-synopsis': [
    { id: 'cj-synopsis', anchor: 'menu-setup-synopsis-tools',
      title: 'Step 1 · Synopsis Builder',
      body:  'Open Setup → Synopsis Tools to generate your story\'s spine from your genre, logline, and cast. This single document becomes the blueprint Claude drafts against every time.' },
  ],
  'cj-outline': [
    { id: 'cj-outline', anchor: 'activity-chapters-btn',
      title: 'Step 2 · Chapter Outline',
      body:  'Open Chapters and add your scenes manually, or click "Draft from beat" to have Claude scaffold all your chapters from the synopsis in one pass. Your story structure lives here.' },
  ],
  'cj-write': [
    { id: 'cj-write', anchor: 'activity-editor-btn',
      title: 'Step 3 · Start Writing',
      body:  'Open any chapter in the Manuscript editor. Empty chapters show a "Draft with AI" shortcut — generate an opening pass and edit from there, or type your own words directly.' },
  ],
  'cj-chat': [
    { id: 'cj-chat', anchor: 'activity-chat-btn',
      title: 'Step 4 · AI Co-Writer',
      body:  'Open Chat to write alongside Claude. Ask it to continue a scene, rewrite a paragraph in your voice, or explain what should happen next. It already has your synopsis and character DNA.' },
  ],
  'cj-bible': [
    { id: 'cj-bible', anchor: 'character-bible-selector',
      title: 'Step 5 · Character Bible',
      body:  'Open the Character Bible and fill in wounds, wants, and speech patterns for each character. A complete wound field alone lifts chapter draft quality measurably in editor reviews.' },
  ],
  'onboarding-start': [
    { id: 'onb-manuscript', anchor: 'menu-setup-import-manuscript',
      title: 'Load a manuscript',
      body:  'Start by importing your own draft (Word / PDF / plain text), OR pick a starter template or synopsis file from your library. This is the raw material The Loom will help you shape.' },
    { id: 'onb-ai-read',    anchor: 'activity-chat-btn',
      title: 'Let the AI read your draft',
      body:  'Once loaded, open "Chat with AI" so the assistant can absorb your voice, characters, and stakes. This unlocks contextual suggestions across the app.' },
    { id: 'onb-bibles',     anchor: 'story-bible-toggle',
      title: 'Check your Bibles',
      body:  'Open the Story Bible and Character Bible next. Make sure the arcs, tones, and cast the AI extracted line up with your intent — edit anything that\'s off before writing.' },
    { id: 'onb-chapters',   anchor: 'activity-chapters-btn',
      title: 'Build chapters (or let AI draft them)',
      body:  'From here you can outline chapters manually, or hand the reins over and let the AI draft chapter one for you to react to. Both paths flow through the same editor.' },
  ],
  'character-bible-open': [
    { id: 'char-selector', anchor: 'character-bible-selector',
      title: 'Pick a character',
      body:  'Search or scroll this dropdown to switch which character you\'re editing. Roles and archetypes show right below each name.' },
    { id: 'char-add', anchor: 'character-bible-add-btn',
      title: 'Add someone new',
      body:  'Click here to add another character to your project roster.' },
  ],
  'story-bible-open': [
    { id: 'story-toggle', anchor: 'story-bible-toggle',
      title: 'Story Bible',
      body:  'Expand this card to review the novel\'s arc, mood, themes, and worldbuilding. The panel scrolls internally so long arcs won\'t push the roster off-screen.' },
    { id: 'story-open-dialog', anchor: 'story-bible-open-dialog',
      title: 'Full-screen view',
      body:  'For a wider, distraction-free read — pops the entire Story Bible into its own scrollable dialog.' },
  ],
  'suggestions-first-open': [
    { id: 'sug-filter', anchor: 'editor-suggestions-filter-pending',
      title: 'Filter suggestions',
      body:  '"Pending" is your inbox — everything the co-editors have flagged for your review. Switch to "All" to see resolved history too.' },
    { id: 'sug-live', anchor: 'editor-suggestions-live',
      title: 'Realtime updates',
      body:  'This pill shows the live-stream status. When it reads "Live", new suggestions appear instantly — no refresh required. If it drops to "Offline" the drawer falls back to a 60-second polling safety net.' },
    { id: 'sug-jump', anchor: 'editor-edit-hint',
      title: 'Jump between suggestions',
      body:  'Press Cmd/Ctrl+J anywhere in the editor to leap to the next unresolved suggestion and briefly pulse its highlight. Shift+Cmd/Ctrl+J goes back.' },
  ],
  // iter55g34 — Payouts ledger contextual tour.
  'payouts-first-open': [
    { id: 'pay-tab', anchor: 'royalty-tab-payouts',
      title: 'Your payouts ledger',
      body:  'Every bookstore sale appears here. Green pills = paid instantly via Stripe Connect at checkout. Blue = paid on the admin sweep. Amber = still pending.' },
    { id: 'pay-filter', anchor: 'payout-filter-paid_direct',
      title: 'Filter by status',
      body:  'Filter chips let you focus on just direct payouts (instant), just manual (swept later), or just pending rows awaiting the next sweep.' },
    { id: 'pay-csv', anchor: 'payouts-csv-btn',
      title: 'Download for records',
      body:  'One click gives you a full CSV of every sale for reconciliation, taxes, or handing off to your accountant.' },
  ],
  // iter55g39 — Editor selection gesture teaching tour.
  'editor-select-all': [
    { id: 'sel-hint', anchor: 'editor-edit-hint',
      title: 'Select the whole chapter',
      body:  'Hold Cmd (mac) or Ctrl (win/linux) and left-click anywhere in the manuscript to select every paragraph. Then Cmd/Ctrl+C copies it — perfect for handing off to a beta reader or an editor.' },
  ],
  'unit-economics-open': [
    { id: 'ue-presets', anchor: 'ue-preset-30d',
      title: 'Period selector',
      body:  'Switch between Today, MTD, Last 30 days, or a custom range. All KPIs, per-plan and per-user rows update instantly.' },
    { id: 'ue-csv', anchor: 'ue-csv-export',
      title: 'CSV export',
      body:  'Grab the per-user table as CSV to pivot in a spreadsheet or feed into your finance tooling.' },
  ],
  'my-billing-open': [
    { id: 'mb-manage', anchor: 'my-billing-manage-btn',
      title: 'Manage on Stripe',
      body:  'Update your card, switch plans, or cancel — all on Stripe\'s hosted portal. Card details never touch our servers.' },
  ],
  // iter-romance25 — Contextual tours for the new bookstore + framework
  // features shipped in Feb 2026. Each fires once per user (via
  // localStorage) the first time the anchor mounts on the page.
  'bookstore-tag-filters-open': [
    { id: 'tag-filter-chips', anchor: 'bs-genre-tag-filters-dark-romance',
      title: 'Hide what you don\'t want to read',
      body:  'Click any content-tag chip (dubcon, violence, obsession…) to hide every title carrying that tag — this shelf and every other. Preferences save to this browser automatically, and the "For You" callout on the landing page will confirm the filter is active.' },
  ],
  'bookstore-for-you-open': [
    { id: 'for-you-callout', anchor: 'bs-for-you-callout',
      title: 'Personalised for you',
      body:  'Any content tag you\'ve excluded on any shelf is now hidden site-wide — spotlight, best-sellers, and up-and-coming. Hit "Show everything" to reset. Manage the full list from Account → Manage what we hide.' },
  ],
  'bookstore-account-warning-manager-open': [
    { id: 'warn-list', anchor: 'bs-account-warning-manager',
      title: 'Your reader-warning shortlist',
      body:  'Every tag you\'ve hidden lives here with the shelf it came from. "Show" un-hides a single tag; "Show everything" clears the whole list.' },
  ],
  'framework-structure-open': [
    { id: 'nf-structure', anchor: 'nf-basics-structure',
      title: '3-Act or 5-Act — your call',
      body:  'Every genre supports both. 3-Act splits chapters into I / II / III (classical). 5-Act adds Rising, Climax, Falling, Denouement (Freytag). Flip anytime — the beat labels update without touching your chapter content.' },
    { id: 'nf-count', anchor: 'nf-basics-count',
      title: 'Up to 25 selectable chapters',
      body:  'Slide the range up to 25 (Romance and Dark Romance shelves are built around 25-chapter beat maps). The framework will scaffold new chapter shells for anything above your current count.' },
  ],
  /* iter-coaching-refresh · Feb-2026 — tours introduced with the
   * Publishing Agreement / Opus Review / Live Stripe rollout. Each
   * tour anchors to a `data-testid` that ships with the corresponding
   * feature. Tours skipped silently when their anchor is missing so
   * the walkthrough never breaks on a slower-loading page. */
  'sign-author-agreement': [
    { id: 'apa-open',       anchor: 'author-agreement-open-btn',
      title: 'Sign your Publishing Agreement',
      body:  'This is the platform-wide contract — 85/15 while your subscription is current, 70/30 if it lapses. Open it here to read the full terms before you sign.' },
    { id: 'apa-typed-name', anchor: 'author-agreement-typed-name',
      title: 'Type your legal name',
      body:  'Your typed name IS your signature under the E-SIGN Act. Match it to the name on file so the executed PDF is legally clean.' },
    { id: 'apa-agree',      anchor: 'author-agreement-agree-checkbox',
      title: 'Tick "I agree"',
      body:  'You must acknowledge the hold-harmless, no-warranty, and indemnity sections before the Sign button unlocks.' },
    { id: 'apa-sign',       anchor: 'author-agreement-sign-btn',
      title: 'Send to Company for countersignature',
      body:  'Your signed row lands in the Admin queue. Once an officer countersigns, a tamper-sealed PDF appears in your dashboard for download.' },
  ],
  'admin-author-agreements-open': [
    { id: 'apa-adm-filter', anchor: 'apa-admin-filter-pending_admin',
      title: 'Filter to Pending',
      body:  'The queue defaults to "Pending Admin" — those are contracts already signed by the author waiting on Company countersignature.' },
    { id: 'apa-adm-open',   anchor: 'apa-admin-row-open',
      title: 'Open a contract',
      body:  'Click a row to preview the exact text the author saw. The SHA-256 tamper seal is verified before you can countersign.' },
    { id: 'apa-adm-sign',   anchor: 'apa-admin-countersign-btn',
      title: 'Countersign',
      body:  'Type your legal name and confirm your role (default: Authorised Officer). The executed PDF is generated and cached in the row.' },
    { id: 'apa-adm-reject', anchor: 'apa-admin-reject-btn',
      title: 'Or reject with a reason',
      body:  'If the author needs to re-sign (name mismatch, wrong version), reject with a short reason. The author sees it on their dashboard and re-signs.' },
  ],
  'admin-stripe-live-banner-open': [
    { id: 'stripe-banner',   anchor: 'admin-stripe-mode-banner',
      title: 'Stripe mode at a glance',
      body:  'Green pill = Live, keys + webhook verified. Amber = Live but a piece is missing. Sky = Test mode (safe for staging). Red = not configured — set STRIPE_API_KEY in the deployment env.' },
    { id: 'stripe-hint',     anchor: 'admin-stripe-mode-hint',
      title: 'Fix-it hint',
      body:  'Whatever the pill can\'t verify (webhook, publishable key, secret) is called out in the hint next to the label — hover for the exact env-var to set.' },
  ],
  /* iter-coaching-refresh · Feb-2026 — WHATS_NEW is the canonical
   * "what changed since your last visit" walkthrough. `runWhatsNewIfDue`
   * aliases this array to the current `COACHING_VERSION` id so we can
   * bump the version and re-run without editing every consumer.
   *
   * Each step may carry an optional `videoUrl` — when present the tour
   * tooltip renders an inline autoplaying 15-second clip above the
   * body copy so returning authors *see* the change before touching a
   * button. Missing videos degrade gracefully to text-only steps. */
  'whats-new': [
    { id: 'wn-arc-walker',  anchor: 'chapters-arc-walker-btn',
      title: 'New · Story Arc Walker',
      body:  'The "Arc" button in the Chapters panel runs a Claude-powered analysis of every chapter — tracking character arcs, continuity gaps, tone/voice drift, and returning a 0-100 publishable score. 85+ means ready to ship.' },
    { id: 'wn-title-wand',  anchor: 'chapter-suggest-title-btn',
      title: 'New · AI Chapter Title Wand',
      body:  'Hover any chapter row and click the wand icon to have Claude suggest a title based on the chapter content. Accept in one click or edit inline.' },
    { id: 'wn-global-rename', anchor: 'global-rename-btn',
      title: 'New · Global Manuscript Rename',
      body:  'The pencil icon beside the manuscript title at the top of the Chapters panel renames your project everywhere at once — project record, Loom catalog, and bookstore listing.' },
    { id: 'wn-agreement',  anchor: 'author-agreement-open-btn',
      title: 'Earlier · Author Publishing Agreement',
      body:  'The platform-wide contract now lives on your dashboard. Sign once (85/15 while subscribed, 70/30 if you leave) and download the countersigned PDF from the same card.',
      videoUrl: '/api/help/feature-video/author-agreement-sign' },
  ],

  /* ── New tools (Feb-2026) ──────────────────────────────────────────────
   * Tour entries for: AI Chapter Title Suggester, Global Manuscript Rename,
   * Story Arc Walker, Character Bible AI assist, Relationship Map,
   * Voice Coach, and manual Save button. */
  'chapters-new-tools': [
    { id: 'cnt-rename', anchor: 'global-rename-btn',
      title: 'Rename your manuscript everywhere',
      body:  'Click the pencil icon beside the manuscript title to rename it — this syncs the new name across your project, The Loom catalog, and any bookstore listing in one step.' },
    { id: 'cnt-wand', anchor: 'chapter-suggest-title-btn',
      title: 'AI chapter title wand',
      body:  'Hover a chapter row and click the wand icon. Claude reads the chapter content and suggests a compelling title — you can accept it, edit it, or dismiss.' },
    { id: 'cnt-arc', anchor: 'chapters-arc-walker-btn',
      title: 'Story Arc Walker',
      body:  'Tap "Arc" in the toolbar to run the Story Arc Walker. Claude analyzes every chapter for character arc continuity, story threads, tone drift, and voice drift — and returns a 0-100 publishable score. 85+ means your manuscript is ready.' },
  ],
  'arc-walker-open': [
    { id: 'aw-score', anchor: 'arc-walker-score-circle',
      title: 'Publishable score',
      body:  'This 0-100 score is the Arc Walker\'s overall assessment. 85+ means Claude considers the manuscript ready for submission or self-publishing. Below 85, check the breakdown bars to see which area to fix first.' },
    { id: 'aw-rerun', anchor: 'arc-walker-rerun-btn',
      title: 'Re-run after revisions',
      body:  'Click "Re-run" any time to get a fresh analysis. The score also auto-updates after each AI chapter generation — no need to re-run manually every time.' },
    { id: 'aw-gaps', anchor: 'arc-walker-gaps-section',
      title: 'Continuity gaps',
      body:  'High-severity gaps are blocking issues (a character dies then reappears, a date changes). Medium and low are polish items. Fix high ones before publishing.' },
  ],
  'character-bible-ai-tools': [
    { id: 'cb-archetype', anchor: 'character-archetype-suggest-btn',
      title: 'AI archetype suggestion',
      body:  'The wand next to the Archetype field sends the character\'s name, role, and description to Claude and picks the best-fit archetype from the 77-item catalog. You can accept or override.' },
    { id: 'cb-age', anchor: 'character-age-suggest-btn',
      title: 'AI age estimation',
      body:  'No age on file? The sparkle button next to Age asks Claude to estimate based on the character\'s role, archetype, and description.' },
    { id: 'cb-relmap', anchor: 'relationship-map-toggle',
      title: 'Relationship Map',
      body:  'Click the network icon to visualise how characters relate to each other — family, romance, rivalry. Each edge is editable and feeds the AI context.' },
  ],
  'save-and-voice-tools': [
    { id: 'sv-save', anchor: 'activity-save-btn',
      title: 'Manual Save',
      body:  'The floppy-disk icon in the activity bar triggers an immediate save of the open chapter — useful before switching chapters or stepping away from the computer.' },
    { id: 'sv-voice', anchor: 'chapters-voice-coach-btn',
      title: 'Voice Coach',
      body:  'Tap "Voice" in the Chapters toolbar to audit every chapter for regional-dialect consistency and prose-register drift. Individual chapter rows also have a per-chapter "Voice" check on hover.' },
  ],

  /* Feb-2026 — "The Process": a cinematic, on-demand walkthrough of
   * the complete write → edit → suggest → proofread loop, launched
   * from the Sparkles icon in ActivityBar. Deliberately separate from
   * the Quickstart checklist (which lists tasks to tick off) — this
   * one spotlights the real controls live, in order, for true
   * first-timers. Re-runnable anytime via `{ force: true }`. */
  'the-process': [
    { id: 'proc-open',   anchor: 'activity-editor-btn',
      title: 'Step 1 · The Manuscript workspace',
      body:  'This icon toggles your manuscript workspace — where every chapter gets written and edited. It\'s already open for most projects; leave it open and click Next below (clicking the icon itself would close it since it\'s a toggle).' },
    { id: 'proc-edit',   anchor: 'manuscript-prose',
      title: 'Step 2 · Write, generate, or edit Chapter One',
      body:  'If the chapter is empty, look for "…or Draft with AI" to have your project\'s AI model write an opening pass. Otherwise, click anywhere in the text to edit directly — fix a line, tighten a paragraph, or rewrite a scene.' },
    { id: 'proc-suggest', anchor: 'editor-suggestions-btn',
      title: 'Step 3 · Get AI suggestions',
      body:  'Coach Hints reads your chapter and flags pacing, voice, and continuity issues live — click the lightbulb anytime for a fresh pass.' },
    { id: 'proc-proof',  anchor: 'editor-plagiarism-btn',
      title: 'Step 4 · Proofread before you publish',
      body:  'Run an originality + proofreading scan here. It proofreads using whichever AI model you\'ve set as this project\'s model — no separate setup needed.' },
    { id: 'proc-model',  anchor: 'open-settings-button',
      title: 'Step 5 · Pick your proofreader',
      body:  'Want Opus, Sonnet, GPT, or Gemini to handle it instead? Switch your project\'s AI model here in Settings — drafting, suggestions, and proofreading all follow your pick.' },
  ],

  /* iter-train1 — "Start producing content" master training sequence.
   *
   * A logical, ordered 8-step curriculum for authors who have never
   * shipped a chapter with The Loom. Each lesson is a bite-sized tour
   * anchored to the real UI (not a slideshow), so the writer *does*
   * each step instead of just reading about it. Progress persists to
   * localStorage so the Training Center can render checkmarks and let
   * writers pick up where they left off.
   *
   * Structure of the curriculum:
   *   L1  Orient — the workspace panels
   *   L2  Seed the story — synopsis / template / paste
   *   L3  Build the framework — genre + logline + 25-beat plan
   *   L4  Fill the bibles — character + story
   *   L5  Preview the prompt — audit before spending tokens
   *   L6  Draft chapter one — first prose from Claude
   *   L7  Polish — suggestions + voice check + revise
   *   L8  Ship — publish to bookstore (or bulk-draft on Premier)
   */
  'train-l1-orient': [
    { id: 't1-menu', anchor: 'menubar-file',
      title: 'Lesson 1 · Meet the menu',
      body:  'Everything you build starts from here. File → New Project, File → New from Template, or File → Import Synopsis. The workspace panels below are toggled from the icon bar on the left.' },
    { id: 't1-panels', anchor: 'activity-editor-btn',
      title: 'The panel bar',
      body:  'Each icon toggles one panel — Manuscript, Chapters, Story Bible, Character Bible, Chat. Turn on only what you need for the task at hand; the rest hide out of the way.' },
    { id: 't1-bookshelf', anchor: 'activity-bookshelf-btn',
      title: 'Your bookshelf, always visible',
      body:  'Toggle this to see every draft, finished, and published book at a glance. It\'s where you\'ll come back to publish, boost, or continue a manuscript.' },
  ],
  'train-l2-seed': [
    { id: 't2-new',      anchor: 'menu-new-project',
      title: 'Lesson 2 · Seed the story',
      body:  'Three ways in. "New Project" is a blank slate. Use it when the story is already in your head and you just want a shell to write into.' },
    { id: 't2-template', anchor: 'menu-setup-new-from-template',
      title: 'Or pick a template',
      body:  'Start from Romance25, Mystery25, or Fantasy25 — a full 25-beat plan with characters + world DNA already wired. Fastest path from blank page to draftable chapter.' },
    { id: 't2-import',   anchor: 'menu-import-manuscript',
      title: 'Or paste what you\'ve got',
      body:  'Already have prose in Word, PDF, or plain text? Import brings it in and asks Claude to extract characters, arcs, and stakes so you\'re not starting over.' },
  ],
  'train-l3-framework': [
    { id: 't3-basics',   anchor: 'nf-basics-structure',
      title: 'Lesson 3 · Structure your novel',
      body:  '3-Act or 5-Act — either scaffolds the same beats, just labelled differently. Slide chapters up to 25 for Romance / Dark Romance. This is where Claude\'s draft plan lives.' },
    { id: 't3-logline',  anchor: 'nf-new-logline',
      title: 'The logline',
      body:  'One sentence that names the protagonist, their stakes, and the antagonist. Everything Claude drafts serves this line — a strong logline lifts every chapter.' },
    { id: 't3-preview',  anchor: 'nf-prompt-audit-open',
      title: 'Preview what Claude sees',
      body:  'Click here anytime to see the exact system + user prompt Claude will get for a chapter — no tokens spent. Trust the plan before you draft.' },
  ],
  'train-l4-bibles': [
    { id: 't4-char',     anchor: 'character-bible-selector',
      title: 'Lesson 4 · Fill the Character Bible',
      body:  'Every character deserves a wound, a want, and a voice sample. Adding a wound alone lifts chapter drafts ~30% in editor rating — the biggest single lever.' },
    { id: 't4-story',    anchor: 'story-bible-toggle',
      title: 'And the Story Bible',
      body:  'World, themes, tone, and shelf manifesto. These act as guardrails so Claude never drifts into a tonally-off scene.' },
  ],
  'train-l5-audit': [
    { id: 't5-open',     anchor: 'nf-prompt-audit-open',
      title: 'Lesson 5 · Audit your prompt',
      body:  'Open the Prompt Audit drawer on the Framework page. It shows every populated field (green) and every high-impact blank (red) with a lift estimate — fix the reds first.' },
  ],
  'train-l6-draft': [
    { id: 't6-chapters', anchor: 'activity-chapters-btn',
      title: 'Lesson 6 · Draft chapter one',
      body:  'Open the Chapters panel, pick chapter 1, and click "Draft from beat" — Claude will honor every Bible + Framework field you\'ve filled. First run typically takes 20-40 s.' },
  ],
  'train-l7-polish': [
    { id: 't7-suggest',  anchor: 'editor-suggestions-filter-pending',
      title: 'Lesson 7 · Read the suggestions',
      body:  'Once a chapter drafts, editorial suggestions land in this drawer live. Filter to "Pending" for what needs your review; use Cmd/Ctrl+J to jump between them.' },
  ],
  'train-l8-ship': [
    { id: 't8-bookshelf', anchor: 'activity-bookshelf-btn',
      title: 'Lesson 8 · Ship it',
      body:  'Open My Bookshelf and mark the manuscript finished when it\'s ready. The "Publish to Bookstore" button on the Finished card takes you live in one click, with a 70/30 revenue share.' },
    { id: 't8-bulk',      anchor: 'menu-bulk-novel-wizard',
      title: 'Premier only — Bulk Wizard',
      body:  'On Premier Studio? Queue up to 5 novels at once from templates + voice-lock them to your book 1 so a whole series drafts overnight in your authorial register.' },
  ],

  /* iter-storystart · Sep-2026 — Event-driven nudges through the real
   * writing lifecycle (not Training-Center-driven — these fire on their
   * own the moment the underlying condition is true, once per browser). */
  'author-begin-story': [
    { id: 'begin-canvas', anchor: 'manuscript-blank-canvas',
      title: 'Begin your story here',
      body:  'Type your opening line right in this frame — it auto-saves the moment you click away. Not feeling it yet? Hover the frame and click "…or Draft with AI" to let Claude take the first pass.' },
  ],
  'author-ai-next-step': [
    { id: 'ai-next', anchor: 'activity-chat-btn',
      title: 'You\'re off — now ask Claude what\'s next',
      body:  'Open Chat with AI and ask it to read what you\'ve written so far. It can suggest the next beat, tighten a paragraph, or draft the rest of the scene in your voice.' },
  ],
  'author-request-editor-review': [
    { id: 'ready-editor', anchor: 'activity-bookshelf-btn',
      title: 'Finished a draft? Get a professional pass first',
      body:  'Before you publish, open File → Request Editor Review to have a live editor read this manuscript and send back suggested edits. Once you\'re happy, come back to My Bookshelf and hit "Publish to Bookstore" to go live.' },
  ],
};

/* iter-train1 — Training Center curriculum manifest. Read by
 * `TrainingCenter.jsx` to render the ordered lesson list, and by
 * `runTrainingLesson` to fire the underlying `runTour`. Kept as data
 * (not JSX) so lessons stay easy to reorder or extend. */
export const TRAINING_LESSONS = [
  { id: 'train-l1-orient',    order: 1, minutes: 2, title: 'Orient — the workspace panels',
    goal: 'Know where every panel lives so you can shape the workspace to the task.' },
  { id: 'train-l2-seed',      order: 2, minutes: 3, title: 'Seed the story',
    goal: 'Pick the fastest path in: blank, template, or import.' },
  { id: 'train-l3-framework', order: 3, minutes: 5, title: 'Structure your novel',
    goal: 'Set genre, logline, act structure, and chapter count — the plan Claude drafts against.' },
  { id: 'train-l4-bibles',    order: 4, minutes: 8, title: 'Fill the Bibles',
    goal: 'Wound + want + voice per character; world + tone + themes for the story.' },
  { id: 'train-l5-audit',     order: 5, minutes: 2, title: 'Preview the prompt',
    goal: 'Confirm Claude has everything before you spend tokens.' },
  { id: 'train-l6-draft',     order: 6, minutes: 3, title: 'Draft chapter one',
    goal: 'Turn the plan into real prose — first real payoff.' },
  { id: 'train-l7-polish',    order: 7, minutes: 4, title: 'Polish',
    goal: 'Work through editorial suggestions and voice-consistency flags.' },
  { id: 'train-l8-ship',      order: 8, minutes: 3, title: 'Ship it',
    goal: 'Mark finished, publish to Bookstore, boost.' },
  // New tools — Feb-2026
  { id: 'chapters-new-tools', order: 9, minutes: 4, title: 'AI Chapter Title Wand + Rename + Arc Walker',
    goal: 'Use the three new Chapters-panel tools: wand for AI title suggestions, pencil to globally rename the manuscript, and Arc Walker for continuity + publishable score.' },
  { id: 'character-bible-ai-tools', order: 10, minutes: 3, title: 'Character Bible AI tools',
    goal: 'Let AI suggest a character\'s archetype and age, and visualize relationships on the Relationship Map.' },
  { id: 'save-and-voice-tools', order: 11, minutes: 2, title: 'Manual Save & Voice Coach',
    goal: 'Save your manuscript any time with the activity-bar button, and run the Voice Coach to catch regional-dialect drift.' },
];

const LESSON_STATE_KEY = 'pw_training_lesson_state_v1';

function _loadState() {
  try { return JSON.parse(window.localStorage.getItem(LESSON_STATE_KEY) || '{}'); }
  catch { return {}; }
}
function _saveState(s) {
  try { window.localStorage.setItem(LESSON_STATE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function getTrainingProgress() {
  const state = _loadState();
  return TRAINING_LESSONS.map((l) => ({ ...l, completed: !!state[l.id] }));
}

export function markLessonComplete(id) {
  const state = _loadState();
  const wasAlready = !!state[id];
  if (!wasAlready) {
    state[id] = new Date().toISOString();
    _saveState(state);
  }
  try {
    window.dispatchEvent(new CustomEvent('training-progress-changed'));
  } catch { /* ignore */ }
  // iter-training-v-ach-coach — Award the matching achievement (idempotent
  // on the server). Fire-and-forget; the achievements lib caches locally
  // so replays don't spam the API.
  if (!wasAlready && id && id.startsWith('train-l')) {
    // Map `train-l1-orient` → `training:l1-orient`.
    const achKey = 'training:' + id.replace(/^train-/, '');
    import('@/lib/achievements')
      .then(({ award }) => award(achKey))
      .catch(() => { /* ignore */ });
    // iter-p2 — Nudge the writer toward the next lesson via a toast.
    // Fires once per lesson (guarded by `wasAlready`) so it never
    // re-hazes on tour replays. iter-p2-b — writers can silence this
    // stream via `pw_disable_next_lesson_toast` (Training Center gear).
    let toastDisabled = false;
    try { toastDisabled = window.localStorage.getItem('pw_disable_next_lesson_toast') === '1'; }
    catch { /* ignore */ }
    if (toastDisabled) return;
    const idx = TRAINING_LESSONS.findIndex((l) => l.id === id);
    const next = idx >= 0 ? TRAINING_LESSONS[idx + 1] : null;
    if (next) {
      import('sonner').then(({ toast }) => {
        toast.success(`Lesson ${idx + 1} complete — up next: L${next.order} · ${next.title}`, {
          duration: 5200,
          action: {
            label: 'Start L' + next.order,
            onClick: () => runTrainingLesson(next.id),
          },
        });
      }).catch(() => { /* ignore */ });
    } else if (idx >= 0) {
      import('sonner').then(({ toast }) => {
        toast.success('Curriculum complete — you\'re production-ready.', { duration: 4200 });
      }).catch(() => { /* ignore */ });
    }
  }
}

export function resetTraining() {
  _saveState({});
  try { window.dispatchEvent(new CustomEvent('training-progress-changed')); } catch { /* ignore */ }
}

export function runTrainingLesson(id, opts = {}) {
  runTour(id, { force: true, ...opts });
  // Mark complete once the user starts — the whole tour is 1-3 steps
  // long, and forcing "must complete every step" turns into a UX
  // punishment loop. Progress serves as "you've been through this".
  setTimeout(() => markLessonComplete(id), 400);
}

export function runTour(id, opts = {}) {
  const steps = TOURS[id];
  if (!steps) return;
  // Off-mode users get no tours — UNLESS this is an explicit, user-
  // initiated re-launch (opts.force), e.g. clicking "The Process" or
  // a Training Center lesson. `onboarding-start` already bypassed this
  // for the same reason; any `force` call should too.
  if (id !== 'onboarding-start' && !opts.force && promptMode() === 'off') return;
  startContextualTour(id, steps, opts);
}

export { resetTour };

/* iter-coaching-refresh · Feb-2026
 * ---------------------------------------------------------------
 * COACHING_VERSION tracks the last material change to the tour
 * catalogue OR to a coached button/function label. Bump it whenever:
 *   • a new TOUR is added below
 *   • an existing button/anchor is renamed
 *   • a new WHATS_NEW step is added
 * When Guided or Light mode is active and the stored user version
 * differs from COACHING_VERSION, `runWhatsNewIfDue()` fires the
 * `whats-new` tour once, then persists the new version so subsequent
 * logins don't re-nag.
 * -------------------------------------------------------------- */
export const COACHING_VERSION = '2026-02-24.1';
const COACHING_SEEN_KEY = 'pw_coaching_seen_version';

export function getCoachingSeenVersion() {
  try { return window.localStorage.getItem(COACHING_SEEN_KEY) || null; }
  catch { return null; }
}

export function markCoachingSeen(version = COACHING_VERSION) {
  try { window.localStorage.setItem(COACHING_SEEN_KEY, version); }
  catch { /* ignore */ }
}

/**
 * Run the `whats-new-since-<version>` sequence exactly once whenever
 * the coaching catalogue's version has moved since the user last saw
 * it. `runTour` respects `pw:tour-<id>-done`, so we intentionally
 * `force` here on the first mismatch and persist the seen version so
 * we don't spam on subsequent renders.
 *
 * Off-mode users are skipped so the "no prompts" preference is
 * honoured. Guided AND Light both receive the update — the intent is
 * "learn any functions or buttons that changed", which both modes opt
 * into.
 */
export function runWhatsNewIfDue(opts = {}) {
  const mode = promptMode();
  if (mode === 'off' && !opts.force) return false;
  const seen = getCoachingSeenVersion();
  if (seen === COACHING_VERSION && !opts.force) return false;
  // Fire the WHATS_NEW tour steps. `runTour` uses per-id "done"
  // localStorage keys, so we use a fresh id per version — that way a
  // returning user who saw v2026-01 doesn't have `2026-02` shadowed.
  const id = `whats-new-${COACHING_VERSION}`;
  if (!TOURS[id]) TOURS[id] = TOURS['whats-new'];   // alias to latest set
  startContextualTour(id, TOURS[id], { force: true });
  markCoachingSeen(COACHING_VERSION);
  return true;
}

